// Copyright 2021 Sorama B.V.

import { computedFrom } from "aurelia-framework";
import { IPagedRequest } from "./IWebRequestHandler";

/**
 * Paged request implementation for serving arrays as paged requests.
 */
export class ArrayPagedRequest<T> implements IPagedRequest<T> {
    private _finished: boolean;

    @computedFrom("_finished")
    public get finished(): boolean {
        return this._finished;
    }

    public readonly total: T[] = [];
    private readonly entityPromise: Promise<T[]>;

    private constructor(
        entities: T[] | Promise<T[]>,
        private readonly pageSize: number
    ) {
        let entityPromise = entities as Promise<T[]>;

        if (!entityPromise.then) {
            entityPromise = Promise.resolve(entities as T[]);
        }

        this.entityPromise = entityPromise;
    }

    public next(): Promise<T[]> {
        if (this.pageSize) {
            return this.entityPromise.then((allEntities) => {
                const count = Math.min(this.pageSize, allEntities.length - this.total.length);
                const newEntities = allEntities.slice(this.total.length, this.total.length + count);

                if (newEntities.length > 0) {
                    this.total.splice(this.total.length, 0, ...newEntities);
                }

                this._finished = this.total.length === allEntities.length;
                return newEntities;
            });
        } else {
            return this.readToEnd();
        }
    }

    public readToEnd(): Promise<T[]> {
        return this.entityPromise.then((allEntities) => {
            this.total.splice(0, this.total.length, ...allEntities);
            this._finished = true;
            return this.total;
        });
    }

    public getCount(): Promise<number> {
        return this.entityPromise.then((x) => x.length);
    }

    public static create<T>(entities: T[] | Promise<T[]>, pageSize = 0): ArrayPagedRequest<T> {
        return new ArrayPagedRequest(entities, pageSize);
    }
}
